// The reflection asks about the day's moments: a reset-button tap, the biggest loss, a trade taken
// soon after a loss, trades that weren't one of the day's ideas, and the best trade. Answers are
// saved with the day (DayNote.moments) and show on the Notebook's day page.
import { useI18n } from './i18n';
import { currentLocale } from './langState';
import { formatMoney, netPnl } from './pnl';

const MAX = 5;
const timeOf = (d) => new Date(d).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });

export function dayMoments(day, trades, t) {
  const out = [];
  for (const e of day.sessionLog || []) {
    if (e.feeling) out.push({ key: `reset:${new Date(e.time).toISOString()}`, question: t('You hit the {feeling} button at {time}. What set it off?', { feeling: t(e.feeling), time: timeOf(e.time) }) });
  }
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const worst = sorted.reduce((w, x) => (netPnl(x) < 0 && (!w || netPnl(x) < netPnl(w)) ? x : w), null);
  if (worst) out.push({ key: `loss:${worst._id}`, question: t('Your biggest loss was {symbol} at {time} ({money}). What happened?', { symbol: worst.symbol, time: timeOf(worst.date), money: formatMoney(netPnl(worst)) }) });
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const gap = (new Date(sorted[i].date) - new Date(prev.exitDate || prev.date)) / 60000;
    if (netPnl(prev) < 0 && gap >= 0 && gap <= 10) {
      out.push({ key: `fast:${sorted[i]._id}`, question: t(Math.round(gap) === 1 ? 'You took {symbol} 1 minute after a loss. Was it in your plan?' : Math.round(gap) === 0 ? 'You took {symbol} right after a loss. Was it in your plan?' : 'You took {symbol} {n} minutes after a loss. Was it in your plan?', { symbol: sorted[i].symbol, n: Math.round(gap) }) });
      break;
    }
  }
  const ideas = day.ideas || [];
  if (ideas.length) {
    const linked = new Set(ideas.filter((i) => i.tradeId).map((i) => String(i.tradeId)));
    sorted.filter((x) => !linked.has(String(x._id))).slice(0, 2).forEach((x) => {
      out.push({ key: `unplanned:${x._id}`, question: t('{symbol} at {time} wasn’t one of your ideas. Why did you take it?', { symbol: x.symbol, time: timeOf(x.date) }) });
    });
  }
  const best = sorted.reduce((b, x) => (netPnl(x) > 0 && (!b || netPnl(x) > netPnl(b)) ? x : b), null);
  if (best) out.push({ key: `best:${best._id}`, question: t('Your best trade was {symbol} ({money}). What did you do right?', { symbol: best.symbol, money: formatMoney(netPnl(best)) }) });
  return out.slice(0, MAX);
}

export default function Moments({ day, trades, update }) {
  const { t } = useI18n();
  const list = dayMoments(day, trades, t);
  if (!list.length) return null;
  const answers = day.moments || [];
  const answerOf = (key) => (answers.find((m) => m.key === key) || {}).answer || '';
  const answer = (m, text) => {
    const rest = answers.filter((x) => x.key !== m.key);
    update({ moments: text ? [...rest, { key: m.key, question: m.question, answer: text }] : rest });
  };
  return (
    <div className="routine-block moments">
      <span className="routine-block-title">{t('Questions about your day')}</span>
      <span className="field-hint">{t('From your trades and the Live session. Answer the ones that matter.')}</span>
      <ol className="moments-list">
        {list.map((m) => (
          <li key={m.key}>
            <label className="routine-field">
              <span className="moments-q">{m.question}</span>
              <textarea rows="2" maxLength={1000} value={answerOf(m.key)} onChange={(e) => answer(m, e.target.value)} />
            </label>
          </li>
        ))}
      </ol>
    </div>
  );
}
